import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useGuestsContext } from 'hb-react/shared/apps/Events/contexts/GuestsContext';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { FormFields } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/FormFields';
import { RegistrationHelp } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/RegistrationHelp';
import { type FormValues } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/types';
import { requiresBillingPage } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/utils';

import { transform } from './utils/transformer';

export const RegistrationPage = () => {
  const navigate = useNavigate();

  const { currentUser } = useDefaultAppContexts();
  const { event, addons, attendees, actions, donations, metadata } =
    useBooking();

  const initialValues = useMemo(
    () => ({
      attendees,
      addons,
      captcha_response: '',
    }),
    [addons, attendees],
  );

  const guestsContext = useGuestsContext();

  const onSave = useCallback(
    (formValues: FormValues) =>
      event && actions.reset(transform({ ...formValues, event, currentUser })),
    [actions, event, currentUser],
  );

  if (!event || !attendees || attendees.length === 0) {
    return <Loader />;
  }

  const onSubmit = async (values: FormValues, { setErrors }) => {
    try {
      const response = await onSave(values);

      if (!response) {
        return;
      }

      const { cart } = response.data;

      if (guestsContext) {
        guestsContext.actions.saveGuests();
      }

      if (donations.length > 0 && !metadata?.donations_displayed) {
        return navigate('donations');
      }

      if (!requiresBillingPage(cart)) {
        await actions.complete({
          captcha_response: values.captcha_response,
        });

        return navigate('../success');
      }

      navigate('../billing');
    } catch (e) {
      if (e.response?.data?.error_code === 'INCORRECT_CAPTCHA') {
        setErrors({ captcha_response: e.response.data.errors });
      }
    }
  };

  return (
    <>
      <Outlet />

      {event?.registration_help?.enabled && (
        <RegistrationHelp {...event.registration_help} mt={-2} mb={6} />
      )}

      <Box display="block" data-testid="event-registration-form-tickets">
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
        >
          <FormFields />
        </Formik>
      </Box>
    </>
  );
};
